import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import Modal from './Modal';
import { fmtDate } from '../timefmt';

const TYPE_LABELS = {
  InvtPart: 'Inventory', NonInvtPart: 'Non-inventory', Service: 'Service',
  Assembly: 'Assembly', Kit: 'Kit/Package', Group: 'Group', OthCharge: 'Other charge',
};

function StockBadge({ stock }) {
  if (stock == null) return <span className="ps-stock ps-stock-na">—</span>;
  const n = Math.round(stock);
  if (n > 0) return <span className="ps-stock ps-stock-in" title={n + ' available'}>In stock · {n}</span>;
  return <span className="ps-stock ps-stock-out">Out of stock</span>;
}

function PriceCell({ prices }) {
  if (!prices || !prices.length) return <span className="ps-muted">No sales price</span>;
  const p = prices[0];
  const fmt = (v) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const main = p.min === p.max ? fmt(p.min) : fmt(p.min) + ' – ' + fmt(p.max);
  const rest = prices.slice(1).map(x => `${x.currency} ${fmt(x.min)}${x.min !== x.max ? '–' + fmt(x.max) : ''}`).join('  ·  ');
  return (
    <span title={rest ? 'Also priced in: ' + rest : ''}>
      <strong>{p.currency} {main}</strong>
      {rest && <span className="ps-muted ps-more-cur"> +{prices.length - 1}</span>}
    </span>
  );
}

const ATTR_LABELS = { category: 'Category', cct: 'CCT', beam: 'Beam angle', cri: 'CRI', ip: 'IP rating', watt: 'Wattage', cutout: 'Cutout' };
const EMPTY_FILTERS = { category: '', cct: '', beam: '', cri: '', ip: '', watt: '', cutout: '' };

function SpecChips({ r }) {
  const chips = [
    r.cct && ['CCT', r.cct], r.beam && ['Beam', r.beam + '°'], r.cri && ['CRI', r.cri],
    r.ip && ['IP', r.ip], r.watt && ['W', r.watt + 'W'],
  ].filter(Boolean);
  if (!chips.length) return null;
  return (
    <span className="ps-spec-chips">
      {chips.map(([k, v]) => <span key={k} className="ps-spec-chip" title={k}>{v}</span>)}
    </span>
  );
}

function fmt2(v) { return v == null ? '—' : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function HistoryBlock({ title, h, testid }) {
  const s = h?.stats || {};
  return (
    <div className="ps-det-block" data-testid={testid}>
      <h5 style={{ margin: '0 0 4px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em', color: '#4a566b' }}>{title}</h5>
      {!s.count ? <div className="ps-muted" style={{ fontSize: 12 }}>None in the last ~12 months.</div> : (
        <>
          <div className="ps-muted" style={{ fontSize: 12, marginBottom: 4 }}>
            {s.count}× · {s.total_qty} units · unit price {fmt2(s.min_rate)}{s.min_rate !== s.max_rate ? ' – ' + fmt2(s.max_rate) : ''}
            {s.avg_rate != null && <> · avg <strong>{fmt2(s.avg_rate)}</strong></>} · last {s.last_date}
          </div>
          <table className="pc-quote-table" style={{ fontSize: 12 }}>
            <thead><tr><th>Date</th><th>Doc #</th><th>Client</th><th className="num">Qty</th><th className="num">Unit</th><th className="num">Total</th></tr></thead>
            <tbody>
              {(h.recent || []).map((r, i) => (
                <tr key={i}><td style={{ whiteSpace: 'nowrap' }}>{r.date}</td><td>{r.tranid}</td>
                  <td className="ps-det-client" title={r.client}>{r.client || '—'}</td>
                  <td className="num">{r.qty}</td><td className="num">{fmt2(r.rate)}</td><td className="num strong">{fmt2(r.amount)}</td></tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function ItemDetails({ d, itemId }) {
  if (d === 'loading') return <div className="ps-muted" style={{ padding: '8px 0', fontSize: 12 }}>Loading item details…</div>;
  if (d?.error) return <div className="ps-muted" style={{ padding: '8px 0', fontSize: 12 }}>Could not load details: {d.error}</div>;
  const cost = d.cost || {};
  const setup = d.setup_fields || {};
  return (
    <div className="ps-details" data-testid={'ps-details-' + itemId}
      style={{ borderTop: '1px dashed #d4dae5', marginTop: 10, paddingTop: 10, display: 'grid', gap: 12 }}>
      {d.can_view_cost && (
        <div className="ps-det-block" data-testid={'ps-det-cost-' + itemId}>
          <h5 style={{ margin: '0 0 4px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em', color: '#4a566b' }}>Cost setup</h5>
          {cost.purchase_price != null || cost.vendor_name ? (
            <div style={{ fontSize: 13 }}>
              Purchase price <strong data-testid={'ps-det-purchase-' + itemId}>{fmt2(cost.purchase_price)}</strong>
              {cost.vendor_name && <> · vendor <strong>{cost.vendor_name}</strong>{cost.vendor_code ? ` (${cost.vendor_code})` : ''}</>}
              {cost.subsidiary_name && <span className="ps-muted"> · {cost.subsidiary_name}</span>}
              {d.url && <> · <a href={d.url} target="_blank" rel="noreferrer">Open item in NetSuite ↗</a></>}
            </div>
          ) : <div className="ps-muted" style={{ fontSize: 12 }}>No vendor purchase price set up on this item.</div>}
        </div>
      )}
      {(d.price_levels || []).length > 0 && (
        <div className="ps-det-block" data-testid={'ps-det-prices-' + itemId}>
          <h5 style={{ margin: '0 0 4px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em', color: '#4a566b' }}>Price levels</h5>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {d.price_levels.map((p, i) => (
              <span key={i} className="ps-chip" style={{ cursor: 'default' }}>{p.level || 'Base'}: <strong>{p.currency ? p.currency + ' ' : ''}{fmt2(p.price)}</strong></span>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
        <HistoryBlock title="Sold (sales orders)" h={d.history?.orders} testid={'ps-det-orders-' + itemId} />
        <HistoryBlock title="Quoted" h={d.history?.quotes} testid={'ps-det-quotes-' + itemId} />
      </div>
      {d.can_view_cost && Object.keys(setup).length > 0 && (
        <details className="ps-det-block" data-testid={'ps-det-setup-' + itemId}>
          <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#4a566b' }}>Item setup fields ({Object.keys(setup).length})</summary>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '4px 14px', marginTop: 6 }}>
            {Object.entries(setup).map(([k, v]) => (
              <div key={k} style={{ fontSize: 12 }}><span className="ps-muted">{k}:</span> <strong>{v}</strong></div>
            ))}
          </div>
        </details>
      )}
      <div className="ps-muted" style={{ fontSize: 11 }}>{d.history_note}</div>
    </div>
  );
}

export default function ProductSearchView({ toast, isCreator, isAdmin, me, onOpenProduct, onCreateFrom, onCreateQuote }) {
  const [mode, setMode] = useState('netsuite'); // netsuite | datasheets
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);      // null = nothing searched yet
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [stockFilter, setStockFilter] = useState(''); // '' | 'in' | 'out'
  const [limit, setLimit] = useState(25);
  const [filterOpts, setFilterOpts] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [details, setDetails] = useState({}); // itemId -> 'loading' | data | {error}
  const [openDetails, setOpenDetails] = useState({}); // itemId -> bool
  const seq = useRef(0);
  const activeFilters = Object.entries(filters).filter(([, v]) => v && v.trim());
  const filterCount = activeFilters.length + (mode === 'netsuite' && stockFilter ? 1 : 0);
  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.productSearchFilters().then(setFilterOpts).catch(() => setFilterOpts({}));
  }, []);
  const lsKey = 'lpa_recent_searches_' + (me?.id || 'anon');
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem(lsKey)) || []; } catch (e) { return []; }
  });

  function remember(term, m) {
    setRecent(prev => {
      const next = [{ q: term, mode: m, ts: Date.now() },
        ...prev.filter(r => !(r.mode === m && (r.q.toLowerCase() === term.toLowerCase() || term.toLowerCase().startsWith(r.q.toLowerCase()))))].slice(0, 8);
      try { localStorage.setItem(lsKey, JSON.stringify(next)); } catch (e) { /* storage full */ }
      return next;
    });
  }
  function clearRecent() {
    setRecent([]);
    try { localStorage.removeItem(lsKey); } catch (e) { /* noop */ }
  }

  // ---- quote cart (admins — pricing calc access) ----
  const cartKey = 'lpa_ps_cart_' + (me?.id || 'anon');
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(cartKey)) || []; } catch (e) { return []; }
  });
  const [quoteModal, setQuoteModal] = useState(false);
  const [opp, setOpp] = useState('');
  const [oppState, setOppState] = useState({ status: 'idle' }); // idle|checking|found|missing|error
  const saveCart = (next) => {
    setCart(next);
    try { localStorage.setItem(cartKey, JSON.stringify(next)); } catch (e) { /* noop */ }
  };
  const inCart = (key) => cart.some(c => c.key === key);
  const toggleCart = (item) => {
    if (inCart(item.key)) saveCart(cart.filter(c => c.key !== item.key));
    else saveCart([...cart, { ...item, qty: 1 }]);
  };
  const setQty = (key, qty) => saveCart(cart.map(c => c.key === key ? { ...c, qty } : c));

  async function checkOpp(num) {
    const n = (num || '').trim();
    if (!n) { setOppState({ status: 'idle' }); return; }
    setOppState({ status: 'checking' });
    try {
      const data = await api.nsOpportunity(n);
      setOppState({ status: 'found', data });
    } catch (e) {
      setOppState(e.response?.status === 404 ? { status: 'missing' } : { status: 'error', detail: e.response?.data?.detail || e.message });
    }
  }
  function createQuote() {
    if (!opp.trim()) { toast('Enter the opportunity number first', true); return; }
    const items = cart.map(c => ({ ...c, qty: Math.max(1, Number(c.qty) || 1) }));
    setQuoteModal(false);
    saveCart([]);
    onCreateQuote({ opportunity: opp.trim(), items });
  }

  useEffect(() => { setLimit(25); }, [q, mode, filters]);

  useEffect(() => {
    const term = q.trim();
    const fl = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v.trim()) fl[k] = v.trim(); });
    const hasFilters = Object.keys(fl).length > 0;
    if (term.length < 2 && !hasFilters) { setRows(null); setParsed(null); setBusy(false); return; }
    setBusy(true);
    const my = ++seq.current;
    const t = setTimeout(async () => {
      try {
        if (mode === 'netsuite') {
          const { items, parsed: p } = await api.productSearchNs(term, fl, limit);
          if (my === seq.current) { setRows(items || []); setParsed(p || null); if (term && (items || []).length) remember(term, 'netsuite'); }
        } else {
          const { items, parsed: p } = await api.productSearchDs(term, fl, limit);
          if (my === seq.current) { setRows(items || []); setParsed(p || null); if (term && (items || []).length) remember(term, 'datasheets'); }
        }
      } catch (e) {
        if (my === seq.current) {
          setRows([]);
          toast('Search failed: ' + (e.response?.data?.detail || e.message), true);
        }
      }
      if (my === seq.current) setBusy(false);
    }, mode === 'netsuite' ? 550 : 250);
    return () => clearTimeout(t);
  }, [q, mode, filters, limit, toast]);

  const canShowMore = rows && rows.length >= limit && limit < 250;
  const showMore = () => setLimit(l => (l < 100 ? 100 : 250));
  const showMoreBtn = canShowMore && !busy && (
    <div className="ps-showmore" style={{ textAlign: 'center', padding: '10px 0' }}>
      <button className="btn" data-testid="ps-show-more" onClick={showMore}>
        Show more results ({limit < 100 ? 'up to 100' : 'up to 250'})
      </button>
    </div>
  );

  async function toggleDetails(id) {
    const open = !openDetails[id];
    setOpenDetails(o => ({ ...o, [id]: open }));
    if (open && !details[id]) {
      setDetails(dd => ({ ...dd, [id]: 'loading' }));
      try {
        const d = await api.psItemDetails(id);
        setDetails(dd => ({ ...dd, [id]: d }));
      } catch (e) {
        setDetails(dd => ({ ...dd, [id]: { error: e.response?.data?.detail || e.message } }));
      }
    }
  }

  async function openDatasheet(id) {
    try { const p = await api.getProduct(id); onOpenProduct(p); }
    catch (e) { toast('Could not open the datasheet', true); }
  }

  return (
    <div className="psearch" data-testid="product-search-view">
      <div className="ps-hero">
        <h2>Product Search</h2>
        <p className="codegen-sub">Look up any product — search the live NetSuite catalogue or our datasheet library, and see instantly whether an item already has a datasheet.</p>
        <div className="ps-modes" role="tablist">
          <button className={'ps-mode' + (mode === 'netsuite' ? ' on' : '')} data-testid="ps-mode-netsuite" onClick={() => { setMode('netsuite'); setRows(null); }}>NetSuite Items</button>
          <button className={'ps-mode' + (mode === 'datasheets' ? ' on' : '')} data-testid="ps-mode-datasheets" onClick={() => { setMode('datasheets'); setRows(null); }}>Datasheets</button>
        </div>
        <input className="input ps-input" data-testid="ps-search-input" autoFocus
          placeholder={mode === 'netsuite' ? 'Try: downlight, 3000k, 40d beam angle, 80mm cutout' : 'Search datasheets by name, code, SKU or specs (e.g. linear, 3000k)…'}
          value={q} onChange={e => setQ(e.target.value)} />
        <div className="ps-filterbar" data-testid="ps-filterbar">
          <button className={'ps-chip ps-filter-toggle' + (showFilters || filterCount ? ' on' : '')} data-testid="ps-filter-toggle"
            onClick={() => setShowFilters(v => !v)}>
            Filters{filterCount ? ' · ' + filterCount : ''} {showFilters ? '▴' : '▾'}
          </button>
          {parsed && Object.keys(parsed.attrs || {}).length > 0 && (
            <span className="ps-parsed" data-testid="ps-parsed-chips">
              <span className="ps-recent-label">Detected:</span>
              {Object.entries(parsed.attrs).map(([k, v]) => (
                <span key={k} className="ps-chip ps-chip-parsed" data-testid={'ps-parsed-' + k}>{ATTR_LABELS[k] || k}: {v}{k === 'beam' ? '°' : k === 'cutout' ? 'mm' : k === 'watt' ? 'W' : ''}</span>
              ))}
            </span>
          )}
          {filterCount > 0 && (
            <button className="ps-chip ps-chip-clear" data-testid="ps-filters-clear" onClick={() => { setFilters(EMPTY_FILTERS); setStockFilter(''); }}>Clear filters</button>
          )}
        </div>
        {showFilters && (
          <div className="ps-filters" data-testid="ps-filters-panel">
            <label className="ps-filter">
              <span>Category</span>
              <select className="input" data-testid="ps-filter-category" value={filters.category} onChange={e => setFilter('category', e.target.value)}>
                <option value="">Any</option>
                {(filterOpts?.category || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="ps-filter">
              <span>CCT</span>
              <select className="input" data-testid="ps-filter-cct" value={filters.cct} onChange={e => setFilter('cct', e.target.value)}>
                <option value="">Any</option>
                {(filterOpts?.cct || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="ps-filter">
              <span>Beam angle (°)</span>
              <input className="input" data-testid="ps-filter-beam" placeholder="e.g. 40" value={filters.beam} onChange={e => setFilter('beam', e.target.value)} />
            </label>
            <label className="ps-filter">
              <span>CRI</span>
              <select className="input" data-testid="ps-filter-cri" value={filters.cri} onChange={e => setFilter('cri', e.target.value)}>
                <option value="">Any</option>
                {(filterOpts?.cri || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="ps-filter">
              <span>IP rating</span>
              <select className="input" data-testid="ps-filter-ip" value={filters.ip} onChange={e => setFilter('ip', e.target.value)}>
                <option value="">Any</option>
                {(filterOpts?.ip || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="ps-filter">
              <span>Wattage (W)</span>
              <input className="input" data-testid="ps-filter-watt" placeholder="e.g. 12" value={filters.watt} onChange={e => setFilter('watt', e.target.value)} />
            </label>
            <label className="ps-filter">
              <span>Cutout (mm)</span>
              <input className="input" data-testid="ps-filter-cutout" placeholder="e.g. 80" value={filters.cutout} onChange={e => setFilter('cutout', e.target.value)} />
            </label>
            {mode === 'netsuite' && (
              <label className="ps-filter">
                <span>Stock</span>
                <select className="input" data-testid="ps-filter-stock" value={stockFilter} onChange={e => setStockFilter(e.target.value)}>
                  <option value="">Any</option>
                  <option value="in">In stock</option>
                  <option value="out">Out of stock</option>
                </select>
              </label>
            )}
          </div>
        )}
        <div className="ps-hint">{busy ? 'Searching…' : (q.trim().length < 2 && !activeFilters.length) ? 'Type at least 2 characters, or pick a filter. You can also combine specs: "downlight, 3000k, 40d beam angle".' : rows ? rows.length + ' result' + (rows.length === 1 ? '' : 's') : ''}</div>
        {q.trim().length < 2 && !activeFilters.length && recent.length > 0 && (
          <div className="ps-recent" data-testid="ps-recent">
            <span className="ps-recent-label">Recent:</span>
            {recent.map((r, i) => (
              <button key={r.q + r.mode + i} className="ps-chip" data-testid={'ps-recent-chip-' + i}
                title={(r.mode === 'netsuite' ? 'NetSuite Items' : 'Datasheets') + ' search'}
                onClick={() => { setMode(r.mode); setQ(r.q); }}>
                {r.q}<span className="ps-chip-mode">{r.mode === 'netsuite' ? 'NS' : 'DS'}</span>
              </button>
            ))}
            <button className="ps-chip ps-chip-clear" data-testid="ps-recent-clear" onClick={clearRecent} title="Clear recent searches">Clear</button>
          </div>
        )}
      </div>

      {rows && mode === 'netsuite' && (() => {
        const nsRows = !stockFilter ? rows
          : rows.filter(r => stockFilter === 'in'
            ? (r.stock != null && Math.round(r.stock) > 0)
            : (r.stock != null && Math.round(r.stock) <= 0));
        const hidden = rows.length - nsRows.length;
        return (
        <div className="ps-results" data-testid="ps-ns-results">
          {nsRows.length === 0 && !busy && (
            <div className="empty-note" data-testid="ps-ns-empty">
              {stockFilter && rows.length > 0
                ? `No ${stockFilter === 'in' ? 'in-stock' : 'out-of-stock'} items among these ${rows.length} result${rows.length === 1 ? '' : 's'} — items without stock tracking are hidden by this filter.`
                : <>No NetSuite items match {q.trim() ? '“' + q.trim() + '”' : 'the selected filters'}{activeFilters.length ? ' with the selected filters' : ''}.</>}
            </div>
          )}
          {stockFilter && hidden > 0 && nsRows.length > 0 && (
            <div className="ps-hint" data-testid="ps-stock-hidden-note">{hidden} result{hidden === 1 ? '' : 's'} hidden by the {stockFilter === 'in' ? 'In stock' : 'Out of stock'} filter (includes items without stock tracking).</div>
          )}
          {nsRows.map(r => (
            <div className="ps-row" key={r.id} data-testid={'ps-ns-row-' + r.id}>
              <div className="ps-main">
                <div className="ps-name">
                  <span className="ps-code">{r.itemid}</span>
                  {r.displayname && r.displayname !== r.itemid && <span className="ps-dname">{r.displayname}</span>}
                  <span className="ps-type">{TYPE_LABELS[r.itemtype] || r.itemtype}</span>
                </div>
                {r.description && <div className="ps-desc">{r.description}</div>}
                <div className="ps-meta">
                  <PriceCell prices={r.prices} />
                  <StockBadge stock={r.stock} />
                  <SpecChips r={r} />
                  {r.category && <span className="ps-muted ps-cat" title="NetSuite sub category">{r.category}</span>}
                  <button className="ps-chip" data-testid={'ps-expand-' + r.id} onClick={() => toggleDetails(r.id)}
                    title="Prior sales & quote history, price levels and cost setup">
                    {openDetails[r.id] ? 'Hide details ▴' : 'Expand details ▾'}
                  </button>
                </div>
                {openDetails[r.id] && <ItemDetails d={details[r.id]} itemId={r.id} />}
              </div>
              <div className="ps-actions">
                {r.datasheet ? (
                  <>
                    <span className="ps-badge ps-badge-yes" data-testid={'ps-has-ds-' + r.id}>✓ Datasheet attached</span>
                    <button className="btn btn-sm btn-primary" data-testid={'ps-open-ds-' + r.id} onClick={() => openDatasheet(r.datasheet.id)}>Open datasheet</button>
                  </>
                ) : (
                  <>
                    <span className="ps-badge ps-badge-no" data-testid={'ps-no-ds-' + r.id}>✗ No datasheet</span>
                    {isCreator && <button className="btn btn-sm" data-testid={'ps-create-ds-' + r.id} onClick={() => onCreateFrom(r)} title="Open the Editor with this item's name and code pre-filled">Create datasheet</button>}
                  </>
                )}
                {isAdmin && (
                  <button className={'btn btn-sm' + (inCart('ns-' + r.id) ? ' ps-addq-on' : '')} data-testid={'ps-addq-' + r.id}
                    onClick={() => toggleCart({ key: 'ns-' + r.id, code: r.itemid, name: r.displayname || r.itemid, desc: r.description || '', nsItemId: r.id, dsId: r.datasheet?.id || null, dsName: r.datasheet?.name || '' })}
                    title="Add this item to a new quote">
                    {inCart('ns-' + r.id) ? '✓ In quote' : '+ Add to quote'}
                  </button>
                )}
              </div>
            </div>
          ))}
          {showMoreBtn}
        </div>
        );
      })()}

      {rows && mode === 'datasheets' && (
        <div className="ps-results" data-testid="ps-ds-results">
          {rows.length === 0 && !busy && <div className="empty-note">No datasheets match {q.trim() ? `“${q.trim()}”` : 'the selected filters'}{activeFilters.length && q.trim() ? ' with the selected filters' : ''}.</div>}
          {rows.map(p => (
            <div className="ps-row" key={p.id} data-testid={'ps-ds-row-' + p.id}>
              <div className="ps-main">
                <div className="ps-name">
                  {p.code && <span className="ps-code">{p.code}</span>}
                  <span className="ps-dname">{p.name || '(Untitled)'}</span>
                  {p.family && <span className="ps-type">{p.family}</span>}
                  {(p.data?.internalNote || '').trim() && (
                    <span className="lib-note-chip" data-testid={'ps-ds-note-' + p.id}
                      title={'Internal note (website only):\n\n' + p.data.internalNote}>NOTE</span>
                  )}
                </div>
                <div className="ps-meta ps-muted">
                  {[{ core: 'Core Range', customer: 'Customer Specific', personal: 'Personal / Project' }[p.scope] || p.scope,
                    p.updated_at && 'Updated ' + fmtDate(p.updated_at)].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div className="ps-actions">
                <button className="btn btn-sm btn-primary" data-testid={'ps-open-' + p.id} onClick={() => openDatasheet(p.id)}>Open in Editor</button>
                {isAdmin && (
                  <button className={'btn btn-sm' + (inCart('ds-' + p.id) ? ' ps-addq-on' : '')} data-testid={'ps-addq-ds-' + p.id}
                    onClick={() => toggleCart({ key: 'ds-' + p.id, code: p.code || p.name || '', name: p.name || p.code || '', desc: '', nsItemId: null, dsId: p.id, dsName: p.name || '' })}
                    title="Add this product to a new quote">
                    {inCart('ds-' + p.id) ? '✓ In quote' : '+ Add to quote'}
                  </button>
                )}
              </div>
            </div>
          ))}
          {showMoreBtn}
        </div>
      )}

      {isAdmin && cart.length > 0 && (
        <div className="ps-cart-bar" data-testid="ps-cart-bar">
          <div className="ps-cart-items">
            <span className="ps-cart-count" data-testid="ps-cart-count">{cart.length}</span>
            {cart.map(c => c.code || c.name).join(' · ')}
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button className="btn btn-sm" data-testid="ps-cart-clear" onClick={() => saveCart([])}>Clear</button>
            <button className="btn btn-sm btn-primary" data-testid="ps-cart-create" onClick={() => setQuoteModal(true)}>Create quote →</button>
          </div>
        </div>
      )}

      <Modal open={quoteModal} title="Create a quote" onClose={() => setQuoteModal(false)} testid="ps-quote-modal"
        actions={<>
          <button className="btn" onClick={() => setQuoteModal(false)}>Cancel</button>
          <button className="btn btn-primary" data-testid="ps-quote-go" disabled={!opp.trim()} onClick={createQuote}>Create quote →</button>
        </>}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#4a566b' }}>OPPORTUNITY NUMBER *</label>
          <input className="input" data-testid="ps-quote-opp" placeholder="e.g. OPAU025202" value={opp} autoFocus
            onChange={e => { setOpp(e.target.value); setOppState({ status: 'idle' }); }}
            onBlur={() => checkOpp(opp)} onKeyDown={e => { if (e.key === 'Enter') checkOpp(opp); }} />
          <div className="ps-opp-status" data-testid="ps-opp-status">
            {oppState.status === 'checking' && 'Checking NetSuite…'}
            {oppState.status === 'found' && <span className="ps-opp-found">✓ Found in NetSuite{oppState.data?.customer_name ? ' — ' + oppState.data.customer_name : ''}</span>}
            {oppState.status === 'missing' && <span className="ps-opp-missing">Not found in NetSuite — you can still build the quote, but pushing it to NetSuite needs a valid opportunity.</span>}
            {oppState.status === 'error' && <span className="ps-opp-missing">Could not check: {oppState.detail}</span>}
          </div>
        </div>
        <label style={{ fontSize: 12, fontWeight: 700, color: '#4a566b' }}>ITEMS & QUANTITIES</label>
        <div>
          {cart.map(c => (
            <div className="ps-qmodal-row" key={c.key} data-testid={'ps-qmodal-' + c.key}>
              <div className="ps-qmodal-name">
                <strong>{c.code || c.name}</strong>
                {(c.name && c.name !== c.code) || c.desc ? <span>{[c.name !== c.code ? c.name : '', c.desc].filter(Boolean).join(' — ')}</span> : null}
              </div>
              <input className="input ps-qty-input" type="number" min="1" value={c.qty} data-testid={'ps-qty-' + c.key}
                onChange={e => setQty(c.key, e.target.value)} />
              <button className="btn btn-sm" title="Remove" onClick={() => toggleCart(c)}>✕</button>
            </div>
          ))}
          {cart.length === 0 && <div className="empty-note">The quote cart is empty.</div>}
        </div>
        <p style={{ fontSize: 12, color: '#6b7280', marginTop: 12 }}>
          Next you'll land on the Quote Builder with these items pre-loaded — costs pull from NetSuite where available, then pick tiers and save.
        </p>
      </Modal>
    </div>
  );
}
